import { useNavigate } from "react-router-dom";

import { RadioEmpty, RadioFilled } from "@assets";
import { ErrorComponent } from "@components/ErrorComponent";
import { ListItem, ListItemProps } from "@components/ListItem";
import { Loader } from "@components/Loader";
import { MainLayout, MainLayoutProps } from "@components/MainLayout";
import { Spacing, SpacingSizes } from "@components/Spacing";
import { Body } from "@components/Typography";
import { AccountProvider, Routes, ThemeSelection, UserStatus } from "@ctypes";
import { useFeatureFlags, useModal, useTheme, useUser } from "@hooks";
import { Translations, i18n } from "@i18n";
import { Intercom } from "@services";
import { ConfirmAccountClosureModal } from "./ConfirmAccountClosureModal";
import { LogoutButton } from "./LogoutButton";
import { CaptionText, styles } from "./styles";
import { RadioIconProps } from "./types";

export const ProfileRoute = () => {
  const { setTheme } = useTheme();

  const { show } = useModal();
  const navigate = useNavigate();

  const { isEmployerAddressDisabled, loading, error } = useFeatureFlags();
  const { user, isIntegratedPartner, hasBankAccount } = useUser();

  const accountItems: Array<ListItemProps> = [
    {
      title: i18n.t(Translations.ProfileRoutePersonalDetailsTitle),
      subtitle: i18n.t(Translations.ProfileRoutePersonalDetailsSubtitle),
      onClick: () => navigate(Routes.PersonalInformation),
    },
    {
      title: i18n.t(Translations.ProfileRouteDocumentsTitle),
      subtitle: i18n.t(Translations.ProfileRouteDocumentsSubtitle),
      onClick: () => navigate(Routes.Documents),
    },
    {
      title: i18n.t(Translations.ProfileRouteEmploymentInformationTitle),
      subtitle: i18n.t(Translations.ProfileRouteEmploymentInformationSubtitle),
      onClick: () => navigate(Routes.EmploymentInformation),
      hidden: isEmployerAddressDisabled,
    },
    {
      title: i18n.t(Translations.ProfileRoutePlansTitle),
      subtitle: i18n.t(Translations.ProfileRoutePlansSubtitle),
      onClick: () => navigate(Routes.Subscription),
      hidden: user?.status !== UserStatus.Active || isIntegratedPartner || !hasBankAccount,
    },
    {
      title: i18n.t(Translations.ProfileRouteLinkedAccountsTitle),
      subtitle: i18n.t(Translations.ProfileRouteLinkedAccountsSubtitle),
      onClick: () => navigate(Routes.LinkedAccounts),
    },
    {
      title: i18n.t(Translations.ProfileRouteRedeemCodeTitle),
      subtitle: i18n.t(Translations.ProfileRouteRedeemCodeSubtitle),
      onClick: () => navigate(Routes.RedeemCode),
    },
    {
      title: i18n.t(Translations.ProfileRouteTrustedContactTitle),
      subtitle: i18n.t(Translations.ProfileRouteTrustedContactSubtitle),
      onClick: () => navigate(Routes.TrustedContact),
      hidden: user?.provider !== AccountProvider.Alpaca,
    },
  ];

  const displayItems: Array<ListItemProps> = [
    {
      title: i18n.t(Translations.ProfileRouteDisplayLightTitle),
      onClick: () => setTheme(ThemeSelection.Light),
      rightIcon: <RadioIcon selection={ThemeSelection.Light} />,
    },
    {
      title: i18n.t(Translations.ProfileRouteDisplayDarkTitle),
      onClick: () => setTheme(ThemeSelection.Dark),
      rightIcon: <RadioIcon selection={ThemeSelection.Dark} />,
    },
    {
      title: i18n.t(Translations.ProfileRouteDisplaySystemTitle),
      onClick: () => setTheme(ThemeSelection.System),
      rightIcon: <RadioIcon selection={ThemeSelection.System} />,
    },
  ];

  const supportItems: Array<ListItemProps> = [
    {
      title: i18n.t(Translations.ProfileRouteContactUsTitle),
      subtitle: i18n.t(Translations.ProfileRouteContactUsSubtitle),
      onClick: Intercom.showChat,
    },
    {
      title: i18n.t(Translations.ProfileRouteCloseMyAccountTitle),
      subtitle: i18n.t(Translations.ProfileRouteCloseMyAccountSubtitle),
      onClick: () => show(<ConfirmAccountClosureModal />),
    },
  ];

  const props: MainLayoutProps = {
    showTab: true,
  };

  if (loading) return <Loader />;
  if (error) return <ErrorComponent />;

  return (
    <MainLayout {...props}>
      <CaptionText>{i18n.t(Translations.ProfileRouteTitle)}</CaptionText>
      <Spacing size={SpacingSizes.M} />

      {accountItems.map((item) => {
        if (item.hidden) return null;
        return (
          <>
            <ListItem {...item} />
            <Spacing size={SpacingSizes.S} />
          </>
        );
      })}

      <Spacing size={SpacingSizes.XS} />
      <Body style={styles.subheader}>
        {i18n.t(Translations.ProfileRouteDisplayPreferencesTitle)}
      </Body>
      <Spacing size={SpacingSizes.M} />

      {displayItems.map((item) => {
        if (item.hidden) return null;
        return (
          <>
            <ListItem {...item} />
            <Spacing size={SpacingSizes.S} />
          </>
        );
      })}

      <Spacing size={SpacingSizes.XS} />
      <Body style={styles.subheader}>{i18n.t(Translations.ProfileRouteSupportHeader)}</Body>
      <Spacing size={SpacingSizes.M} />

      {supportItems.map((item) => {
        if (item.hidden) return null;
        return (
          <>
            <ListItem {...item} />
            <Spacing size={SpacingSizes.S} />
          </>
        );
      })}
      <Spacing size={SpacingSizes.M} />

      <LogoutButton />
    </MainLayout>
  );
};

const RadioIcon = ({ selection }: RadioIconProps) => {
  const { selection: currentSelection } = useTheme();
  const isSelected = selection === currentSelection;
  return isSelected ? <RadioFilled /> : <RadioEmpty />;
};
